import { Component, EventEmitter, inject, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ModalComponent } from "../../../../shared/modal/modal.component";
import { ConfirmActionModalComponent } from "../../../../shared/confirm-action-modal/confirm-action-modal.component";
import { AlertService } from "../../../../shared/alert/alert.service";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import moment from 'moment-timezone';
import { TimezoneSelectComponent } from "../../../../shared/timezone-select/timezone-select.component";
import { DateHelper } from "../../../../core/helpers/date-helper";
import { LogHelper } from "../../../../core/helpers/log.helper";
import { EDiaryService } from "../../../../core/services/e-diary.service";
import { UpdateEdiaryConfig } from "../../../../core/services/interfaces/update-ediary-config.interface";
import { PatientEdiaryConfig } from "../../../../core/models/patient-ediary-config.model";

@Component({
  selector: 'app-ediary-config-modal',
  templateUrl: './ediary-config-modal.component.html',
  styleUrl: './ediary-config-modal.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class EdiaryConfigModalComponent {
  @ViewChild('modal') modal: ModalComponent;
  @ViewChild('timezoneSelect') timezoneSelect: TimezoneSelectComponent;
  @ViewChild('confirmRemoveReminderModal') confirmRemoveReminderModal: ConfirmActionModalComponent;

  @Input('patientTrialId') patientTrialId: string;
  @Input('hasSubmissions') hasSubmissions: boolean;
  @Input('formId') formId: string;
  @Input('isOnboardingQuestions') isOnboardingQuestions: boolean = false;
  @Input('isIntraDay') isIntraDay: boolean = false;

  @Output('changed') changed = new EventEmitter();

  private readonly _ediaryService = inject(EDiaryService);
  private readonly _alertService = inject(AlertService);

  diaryConfig: PatientEdiaryConfig = new PatientEdiaryConfig();
  loaded = false;
  form: FormGroup;
  editingDisabled = false;
  intraDayReminderTimes: number[] = [];
  newReminderTime: string = '';

  constructor() {
    this.form = new FormGroup({
      isBusy: new FormControl<boolean>(false),
      timezone: new FormControl<string>('', Validators.required),
      timezoneLabel: new FormControl(''),
      originalStartDate: new FormControl(),
      originalEndDate: new FormControl(),
      startDate: new FormControl<string>(''),
      endDate: new FormControl<string>(''),
      remindersEnabled: new FormControl(false),
      emailRemindersEnabled: new FormControl(false),
      reminderTime: new FormControl(''),
      reminderTimeUtc: new FormControl('')
    });

    // Update reminder times when timezone changed
    this.form.get('timezone').valueChanges.subscribe(value => {
      this.updateTimeUtc('reminderTime', 'reminderTimeUtc');
    });

    this.form.get('startDate').valueChanges.subscribe(enabled => {
      this.validateStartEndDates();
    });

    this.form.get('endDate').valueChanges.subscribe(enabled => {
      this.validateStartEndDates();
    });

    this.form.get('remindersEnabled').valueChanges.subscribe(enabled => {
      this.setValidators();
    });

    this.form.get('emailRemindersEnabled').valueChanges.subscribe(enabled => {
      if (this.diaryConfig.mandatoryNotification && !enabled) {
        this.form.patchValue({ emailRemindersEnabled: true });
      }
    });

    // Convert reminder time to UTC
    this.form.get('reminderTime').valueChanges.subscribe(value => {
      this.updateTimeUtc('reminderTime', 'reminderTimeUtc');
    });
  }

  protected setValidators() {
    if (this.form.get('remindersEnabled').value) {
      this.form.get('reminderTime').setValidators(Validators.required);
    } else {
      this.form.get('reminderTime').setValidators(null);
    }
    this.form.get('reminderTime').updateValueAndValidity();
    this.validateStartEndDates();
  }

  isRequired(controlName: string): boolean {
    const control = this.form.get(controlName);
    if (!control) {
      return false;
    }
    const validator = control.validator ? control.validator({} as AbstractControl) : null;
    return validator ? validator.required : false;
  }

  show(): void {
    this.loaded = false;
    this.loadEdiaryConfig();
    this.modal.show();
  }

  hide(): void {
    this.modal.hide();
  }

  shouldShowUtcTime(sourceControl: string): boolean {
    let time = this.form.get(sourceControl).value;
    let timezoneStr = this.form.get('timezone').value;

    return time !== '' && timezoneStr !== null && timezoneStr !== '';
  }

  validateStartEndDates() {
    const originalStartDate = this.form.get('originalStartDate').value;
    const originalEndDate = this.form.get('originalEndDate').value;
    const newStartDate = this.form.get('startDate').value;
    const newEndDate = this.form.get('endDate').value;

    const startDateControl = this.form.get('startDate');
    const endDateControl = this.form.get('endDate');

    const isPeriodic = !this.isOnboardingQuestions && !this.isIntraDay;
    if (isPeriodic) {
      // Periodic diaries always require both dates
      startDateControl.setValidators(Validators.required);
      endDateControl.setValidators(Validators.required);
    } else {
      // Cross-field validation: if one date is set, the other is required
      if (newStartDate) {
        endDateControl.setValidators(Validators.required);
      } else {
        endDateControl.setValidators(null);
      }

      if (newEndDate) {
        startDateControl.setValidators(Validators.required);
      } else {
        startDateControl.setValidators(null);
      }
    }

    // Update validity without emitting events to avoid infinite loop
    startDateControl.updateValueAndValidity({ emitEvent: false });
    endDateControl.updateValueAndValidity({ emitEvent: false });

    if (newStartDate !== originalStartDate && originalStartDate && newStartDate) {
      const diff = newStartDate.getTime() - originalStartDate.getTime();
      if (diff > 0) {
        this.form.patchValue({startDate: originalStartDate});
        this._alertService.showWarningAlert('New start date cannot be after the current start date.');
      }
    }

    if (newEndDate != originalEndDate && newEndDate && originalEndDate) {
      const diff = originalEndDate.getTime() - newEndDate.getTime();
      if (diff > 0) {
        this.form.patchValue({endDate: originalEndDate});
        this._alertService.showWarningAlert('New end date cannot be before the current end date.');
      }
    }
  }

  updateTimeUtc(sourceControl: string, targetControl: string) {
    let time = this.form.get(sourceControl).value;
    let timezoneStr = this.form.get('timezone').value;

    if (time === null || time === '' || timezoneStr === null || timezoneStr === '') {
      this.form.patchValue({ targetControl: '' });
      return;
    }

    let timezone = this.form.get('timezone').value;
    if (timezone === null || timezone === '')
      return;

    this.form.get(targetControl).setValue(this.convertToUTC(time, timezone));
  }

  /**
   * Loads the diary configuration for the current patient trial.
   * It fetches the configuration via the patient service and
   * assigns it to the local diaryConfig property.
   * Also handles any errors by showing an alert and setting the loaded flag.
   *
   * @return {void} No return value.
   */
  loadEdiaryConfig(): void {
    this._ediaryService.getEdiaryConfig(this.patientTrialId, this.formId).subscribe({
      next: config => {
        this.diaryConfig = config;

        console.log(config);

        // Set daily dose reminders to true if diary entries have not been created yet
        if (config.startDate === null && config.endDate === null)
          config.remindersEnabled = true;

        if (config.reminderTimeMinutes === null) {
          config.reminderTimeMinutes = 540; // 09:00
        }

        this.intraDayReminderTimes = [...(config.intraDayReminderTimes ?? [])].sort((a, b) => a - b);

        this.form.patchValue({
          timezone: config.timezone,
          originalStartDate: config.startDate !== null ? new Date(config.startDate) : null,
          originalEndDate: config.endDate !== null ? new Date(config.endDate) : null,
          startDate: config.startDate !== null ? new Date(config.startDate) : null,
          endDate: config.endDate !== null ? new Date(config.endDate) : null,
          remindersEnabled: config.remindersEnabled,
          emailRemindersEnabled: config.emailRemindersEnabled,
          reminderTime: DateHelper.convertMinutesTo24HourTime(config.reminderTimeMinutes),
        });

        // Set the value on the timezone dropdown
        this.timezoneSelect.setTimezone(config.timezone);

        this.editingDisabled = config.startDate && config.endDate && !this.isOnboardingQuestions;

        this.setValidators();

        this.loaded = true;
      },
      error: (error) => {
        LogHelper.log(error);
        this._alertService.showErrorAlert(error);
        this.loaded = true;
      }
    })
  }

  /**
   * When the timezone is changed we need to get the full text label of the selected timezone to show in the disabled
   * timezone input
   * @param option
   */
  onTimezoneChanged(option: { value: string, text: string }) {
    if (option)
      this.form.patchValue({timezoneLabel: option.text});
  }

  onFormSubmit(): void {
    if (this.form.invalid)
      return;

    this.form.patchValue({isBusy: true});

    const form = this.form;

    let originalStartDate = form.value.originalStartDate;
    let originalEndDate = form.value.orginalEndDate;
    let startDate = form.value.startDate;
    let endDate = form.value.endDate;
    const today = new Date();

    if (startDate && endDate) {
      if (startDate > endDate) {
        this._alertService.showWarningAlert("Start date cannot be after the end date");
        this.form.patchValue({isBusy: false});
        return;
      }

      let startDays = DateHelper.durationDays(startDate, today);
      let startDateDiff = originalStartDate === null ? 0 : startDate.getTime() - originalStartDate.getTime();
      if (startDate < today && startDays > 30 && startDateDiff !== 0) {
        this._alertService.showWarningAlert("Start date cannot be more than 1 month ago");
        this.form.patchValue({isBusy: false});
        return;
      }

      let endDays = DateHelper.durationDays(new Date(), endDate);
      if (endDays > 365) {
        this._alertService.showWarningAlert("End date cannot be more than a year into the future");
        this.form.patchValue({isBusy: false});
        return;
      }
    }

    const dto: UpdateEdiaryConfig = {
      startDate: DateHelper.dateToYyyyMmDd(form.value.startDate),
      endDate: DateHelper.dateToYyyyMmDd(form.value.endDate),
      timezone: form.value.timezone,
      remindersEnabled: form.value.remindersEnabled,
      emailRemindersEnabled: form.value.emailRemindersEnabled,
      reminderTimeMinutes: DateHelper.convert24HourTimeToMinutes(form.value.reminderTime),
      reminderTimeMinutesUtc: DateHelper.convert24HourTimeToMinutes(form.value.reminderTimeUtc),
      intraDayReminderTimes: this.isIntraDay ? this.intraDayReminderTimes : undefined
    }

    this._ediaryService.updateEdiaryConfig(this.patientTrialId, dto, this.formId).subscribe({
      next: () => {
        this.form.patchValue({isBusy: false});
        this._alertService.showSuccessAlert('Settings updated successfully');
        this.changed.emit();
        this.hide();
      },
      error: error => {
        LogHelper.log(error);
        this._alertService.showErrorAlert(error);
        this.form.patchValue({isBusy: false});
      }
    })
  }

  formatReminderTime(minutes: number): string {
    return DateHelper.convertMinutesTo24HourTime(minutes);
  }

  addReminderTime(): void {
    if (!this.newReminderTime) return;
    const minutes = DateHelper.convert24HourTimeToMinutes(this.newReminderTime);
    if (this.intraDayReminderTimes.includes(minutes)) return;
    this.intraDayReminderTimes = [...this.intraDayReminderTimes, minutes].sort((a, b) => a - b);
    this.newReminderTime = '';
  }

  onRemoveReminderTimeClicked(minutes: number): void {
    this.confirmRemoveReminderModal.showConfirmation(minutes.toString());
  }

  removeReminderTime(minutesStr: string): void {
    const minutes = parseInt(minutesStr, 10);
    this.intraDayReminderTimes = this.intraDayReminderTimes.filter(t => t !== minutes);
  }

  convertToUTC(time: string, timezone: string): string {
    // Parse the input time and timezone
    const timeInZone = moment.tz(time, "HH:mm", timezone);

    // Convert to UTC and format it as a 24-hour string
    const timeInUTC = timeInZone.utc().format("HH:mm");

    return timeInUTC;
  }

}
